import { useState, useEffect, useCallback } from 'react';
import { useTenantContext } from '@/components/providers/TenantProvider';
import { z } from 'zod';

// Schema for validating favorite properties from localStorage
const FavoritePropertySchema = z.object({
  id: z.union([z.number(), z.string()]),
  title: z.string(),
  image: z.string().optional(),
  price: z.string().optional(),
  address: z.string().optional(),
  savedAt: z.string(),
});

type ValidatedFavoriteProperty = z.infer<typeof FavoritePropertySchema>;

const FavoritesArraySchema = z.array(FavoritePropertySchema);

interface FavoriteProperty {
  id: number | string;
  title: string;
  image?: string;
  price?: string;
  address?: string;
  savedAt: string;
}

const STORAGE_KEY_PREFIX = 'favorites_';
const MAX_STORAGE_SIZE = 512 * 1024; // 512KB limit

/**
 * Safely parse and validate favorites from localStorage
 */
function parseAndValidateFavorites(stored: string | null): FavoriteProperty[] {
  if (!stored) return [];
  
  // Check size limit
  if (stored.length > MAX_STORAGE_SIZE) {
    console.warn('Favorites storage too large, clearing');
    return [];
  }
  
  try {
    const parsed = JSON.parse(stored);
    const validated = FavoritesArraySchema.safeParse(parsed);
    
    if (validated.success) {
      return validated.data as FavoriteProperty[];
    } else {
      console.warn('Invalid favorites data structure:', validated.error.message);
      return [];
    }
  } catch (error) {
    console.error('Failed to parse favorites:', error);
    return [];
  }
}

/**
 * Hook para gerenciar favoritos de imóveis
 * Os favoritos são salvos no LocalStorage e isolados por tenant
 */
export function useFavorites() {
  const { subdominio } = useTenantContext();
  const storageKey = `${STORAGE_KEY_PREFIX}${subdominio}`;
  
  const [favorites, setFavorites] = useState<FavoriteProperty[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carregar favoritos do localStorage ao montar
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      const validatedFavorites = parseAndValidateFavorites(stored);
      setFavorites(validatedFavorites);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
      setFavorites([]);
    } finally {
      setIsLoading(false);
    }
  }, [storageKey]);

  // Salvar favoritos no localStorage quando mudar
  const saveFavorites = useCallback((newFavorites: FavoriteProperty[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(newFavorites));
      setFavorites(newFavorites);
    } catch (error) {
      console.error('Erro ao salvar favoritos:', error);
    }
  }, [storageKey]);

  // Adicionar aos favoritos
  const addFavorite = useCallback((property: Omit<FavoriteProperty, 'savedAt'>) => {
    const newFavorite: FavoriteProperty = {
      ...property,
      savedAt: new Date().toISOString(),
    };
    
    const updated = [...favorites, newFavorite];
    saveFavorites(updated);
    
    return true;
  }, [favorites, saveFavorites]);

  // Remover dos favoritos
  const removeFavorite = useCallback((propertyId: number | string) => {
    const updated = favorites.filter(fav => fav.id !== propertyId);
    saveFavorites(updated);
    
    return true;
  }, [favorites, saveFavorites]);

  // Toggle favorito (adiciona se não existe, remove se existe)
  const toggleFavorite = useCallback((property: Omit<FavoriteProperty, 'savedAt'>) => {
    const exists = favorites.some(fav => fav.id === property.id);
    
    if (exists) {
      removeFavorite(property.id);
      return false; // Removido
    } else {
      addFavorite(property);
      return true; // Adicionado
    }
  }, [favorites, addFavorite, removeFavorite]);

  // Verificar se um imóvel está nos favoritos
  const isFavorite = useCallback((propertyId: number | string): boolean => {
    return favorites.some(fav => fav.id === propertyId);
  }, [favorites]);

  // Limpar todos os favoritos
  const clearFavorites = useCallback(() => {
    saveFavorites([]);
  }, [saveFavorites]);

  // Obter favorito específico
  const getFavorite = useCallback((propertyId: number | string): FavoriteProperty | undefined => {
    return favorites.find(fav => fav.id === propertyId);
  }, [favorites]);

  // Contar favoritos
  const count = favorites.length;

  return {
    favorites,
    isLoading,
    count,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    getFavorite,
  };
}

/**
 * Hook simplificado para verificar se um imóvel específico é favorito
 */
export function useIsFavorite(propertyId: number | string): boolean {
  const { isFavorite } = useFavorites();
  return isFavorite(propertyId);
}

/**
 * Hook para obter apenas a contagem de favoritos (otimizado para performance)
 */
export function useFavoritesCount(): number {
  const { count } = useFavorites();
  return count;
}
